'use client';

import { useMemo } from 'react';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  isSellable,
  getProductMinQuantity,
  getProductQuantityRules,
  getProductQuantityStep,
  normalizeProductQuantity,
  normalizeProductQuantityDown,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { AskForPriceButton } from '@eliadmin/core/src/components/product/ask-for-price-button';

export function ProductQuantity() {
  const product = useVariantsStore(s => s.product);
  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';
  const settings = useGlobalStore(state => state.templateSettings);

  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(selectedVariant ?? product, {
    isWholesaler,
    priceWithoutVatFirst: settings.product_detail?.price_without_vat_first?.enabled,
  });
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);
  const { t } = useTranslations();

  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const openDialog = useDialogStore(s => s.open);

  //   const selectedServices = useProductStore(state => state.selectedServices);
  const variants = useVariantsStore(state => state.product.variants);
  //   const open = useModalStore(state => state.open);
  //   const close = useModalStore(state => state.close);
  //   const api = useGlobalStore(state => state.config.api);
  //   const decimalPlaces = countDecimals(product.custom_quantity);

  const sellable = isSellable(selectedVariant ? selectedVariant : product, selectedQuantity);
  const quantityRules = useMemo(
    () => getProductQuantityRules(product, selectedVariant),
    [product, selectedVariant],
  );
  const minQuantity = useMemo(() => getProductMinQuantity(quantityRules), [quantityRules]);
  const step = useMemo(() => getProductQuantityStep(quantityRules), [quantityRules]);
  const disabled = !sellable || ((variants?.length || 0) > 0 && !selectedVariant);

  const sortedSuppliers = [...product.suppliers].sort((a, b) => a.position - b.position);

  // main affiliate supplier is only when there is only one supplier
  const firstSupplier = sortedSuppliers[0];

  const handleChangeQuantity = (value: number) => {
    if (!value) {
      value = 0;
    }
    const newValue = normalizeProductQuantity(selectedQuantity + value, quantityRules);
    if (newValue <= 0) {
      setSelectedQuantity(minQuantity);
    } else {
      const stock = selectedVariant ?? product;
      const sellableNext = isSellable(stock, newValue);
      if (!sellableNext && stock.quantity > 0) {
        openDialog({
          id: NotInStockDialog.id,
          content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit} />,
        });
        setSelectedQuantity(normalizeProductQuantityDown(stock.quantity, quantityRules));
      } else if (sellableNext) {
        setSelectedQuantity(newValue);
      }
    }
  };

  if (hasSpecialProductStatus || (!!variants?.length && !selectedVariant)) return null;

  if (priceDisplay.priceOnRequest) {
    return (
      <div
        className={cn(
          'flex w-full flex-col items-start justify-start gap-4 overflow-hidden rounded-[24px] bg-primary-foreground p-5 sm:p-6 md:p-8',
          'ea-custom-product-quantity__container',
        )}
      >
        <AskForPriceButton product={product} variant={selectedVariant} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-4 overflow-hidden rounded-[24px] bg-primary-foreground p-5 sm:p-6 md:p-8',
        'ea-custom-product-quantity__container',
      )}
    >
      <div className="flex w-full items-center justify-between">
        <p className="text-lg font-bold text-black sm:text-2xl">Cena</p>
        <div className="flex flex-col items-end">
          {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
            <span className="text-sm text-muted-foreground">
              {`${t('text1926')} ${formatPrice(
                priceDisplay.mocPrice,
                generalLayout.currency,
                generalLayout.currency_settings,
              )}`}
            </span>
          ) : null}
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-primary sm:text-2xl">
              {formatPrice(
                priceDisplay.current,
                generalLayout.currency,
                generalLayout.currency_settings,
              )}
            </span>
            {priceDisplay.showPreviousPrice ? (
              <del className="text-sm text-muted-foreground">
                {formatPrice(
                  priceDisplay.beforeDiscount,
                  generalLayout.currency,
                  generalLayout.currency_settings,
                )}
              </del>
            ) : null}
          </div>
          {settings.product_detail?.show_price_without_vat?.enabled ? (
            <span className="text-sm text-muted-foreground">
              {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
              {formatPrice(
                priceDisplay.secondary,
                generalLayout.currency,
                generalLayout.currency_settings,
              )}{' '}
              {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
            </span>
          ) : null}
        </div>
      </div>
      <AddToCartButton
        className={cn('h-12 w-full flex-grow', 'ea-product-detail-quantity__add-to-cart-button')}
        quantity={selectedQuantity}
        disabled={disabled}
        product={product}
        sellable={sellable}
        showIcon={true}
      />
    </div>
  );
}
